import { useEffect, useState } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Mensajes unread badge (2026-09-17, Jaime-approved Option 1).
 *
 * Single poller mounted once in AppShell (mirrors the UX-2 notification-bell
 * pattern): GET /messages/conversations every 30s while authenticated, summing
 * unread_count across conversations. Skips ticks while the tab is hidden and
 * does an immediate catch-up on visibilitychange. The count is fanned out to
 * subscribers (IconGridSidebar desktop sidebar + MobileMenu) through a window
 * event, so only ONE poll loop exists no matter how many components read it.
 */
const EVENT = 'vecindus:messages-unread';
let currentCount = 0;

export function useMessagesUnreadPoller(): void {
  const { user } = useAuth();
  const userId = user?.id;
  useEffect(() => {
    if (!userId) {
      // Logout / unauthenticated: clear the badge.
      currentCount = 0;
      window.dispatchEvent(new CustomEvent(EVENT, { detail: 0 }));
      return;
    }
    let alive = true;
    let inFlight = false;
    const refresh = () => {
      if (inFlight) return; // overlap guard
      inFlight = true;
      api.get('/messages/conversations')
        .then((res: any) => {
          if (!alive) return;
          const payload = res?.data?.data ?? res?.data;
          const items = Array.isArray(payload) ? payload : (payload?.items ?? []);
          const total = (items as any[]).reduce(
            (sum, c) => sum + (parseInt(c?.unread_count, 10) || 0),
            0,
          );
          currentCount = total;
          window.dispatchEvent(new CustomEvent(EVENT, { detail: total }));
        })
        .catch(() => { /* transient errors keep the last good count */ })
        .finally(() => { inFlight = false; });
    };
    refresh();
    const POLL_MS = 30000;
    const timer = window.setInterval(() => { if (!document.hidden) refresh(); }, POLL_MS);
    const onVisibility = () => { if (!document.hidden) refresh(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      alive = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [userId]);
}

/** Read the current Mensajes unread count (subscribes to the poller event). */
export function useMessagesUnread(): number {
  const [count, setCount] = useState(currentCount);
  useEffect(() => {
    const onCount = (e: Event) => setCount((e as CustomEvent).detail ?? 0);
    window.addEventListener(EVENT, onCount);
    return () => window.removeEventListener(EVENT, onCount);
  }, []);
  return count;
}
